import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { setIceServers } from "@/features/rtc/iceServers";
import { createRtcLogger, type RtcLogger } from "@/features/rtc/rtcLogger";
import type {
  JoinContract,
  MediaState,
  PeerId,
  PeerInfo,
  RoomEvent,
} from "../types";
import {
  audioConstraints,
  videoConstraints,
  type DevicePrefs,
} from "./useMediaDevices";
import type { RoomTransport } from "./roomTransport";

export type RoomStatus = "idle" | "joining" | "active" | "ended" | "error";

// ── resilience knobs (mirrors the 1:1 screen-share fixes) ────────────────────
const DISCONNECTED_GRACE_MS = 7_000;
const ICE_RESTART_MAX = 2;
const ICE_RESTART_WINDOW_MS = 60_000;
// Connect watchdog: a pc that hasn't reached "connected" this long after
// creation lost its signaling (the initial offer can be published before the
// joiner's first channel subscribe — gone forever, nothing re-offers) — nudge
// the negotiation instead of showing "łączenie…" until the end of time.
const CONNECT_TIMEOUT_MS = 10_000;
const CONNECT_ATTEMPTS_MAX = 3;
// Ignore repeat "renegotiate" requests for the same peer within this window
// (both sides' watchdogs can fire together — one full re-offer is enough).
const RENEGOTIATE_MIN_INTERVAL_MS = 3_000;
// Fresh-TURN-credentials cache horizon for entries created mid-meeting (capped
// further by the server's credential TTL — see currentIceRef).
const ICE_REFRESH_AFTER_MS = 30 * 60_000;
// Burst cap for local-track recovery (a device that keeps dying instantly).
const RECOVER_MAX_ATTEMPTS = 3;
const RECOVER_WINDOW_MS = 30_000;

// Camera default-off threshold: joining a room with this many people present
// starts with the camera disabled (opt-in) to keep mesh encode load sane.
const CAMERA_OFF_AT = 8;

/**
 * Adaptive video quality — the ceiling in a mesh is the SENDER's CPU (one
 * encode per peer connection) and uplink. Recomputed on every join/leave and
 * screen-share toggle; an active screen share bumps cameras one tier down
 * (the screen has priority).
 */
function cameraTierFor(participantCount: number, screenActive: boolean) {
  const TIERS = [
    { maxBitrate: 800_000, scaleResolutionDownBy: 1, maxFramerate: 24 }, // ≤4
    { maxBitrate: 400_000, scaleResolutionDownBy: 2, maxFramerate: 24 }, // 5–6
    { maxBitrate: 250_000, scaleResolutionDownBy: 2, maxFramerate: 15 }, // 7–9
    { maxBitrate: 150_000, scaleResolutionDownBy: 4, maxFramerate: 12 }, // 10–12
  ];
  let tier =
    participantCount <= 4 ? 0 : participantCount <= 6 ? 1 : participantCount <= 9 ? 2 : 3;
  if (screenActive) tier = Math.min(TIERS.length - 1, tier + 1);
  return TIERS[tier];
}

const SCREEN_MAX_BITRATE = 2_000_000;

interface PeerEntry {
  info: PeerInfo;
  pc: RTCPeerConnection;
  polite: boolean;
  makingOffer: boolean;
  ignoreOffer: boolean;
  remoteDescSet: boolean;
  pendingIce: RTCIceCandidateInit[];
  // Exactly TWO transceivers: audio + a SINGLE video channel (camera OR screen,
  // swapped with replaceTrack). Only ONE side of each pair creates them and
  // offers (the deterministic initiator, myId > peerId); the responder lets the
  // browser create matching transceivers from the incoming offer. This removes
  // glare entirely — two Chrome peers offering simultaneously produced the
  // "BUNDLE codec collision for header extension" SDP error on re-offer.
  isInitiator: boolean;
  transceivers: {
    audio: RTCRtpTransceiver;
    video: RTCRtpTransceiver;
  } | null;
  remoteStream: MediaStream; // audio + the one video (camera or screen)
  mediaState: MediaState;
  connectionState: string;
  graceTimer: ReturnType<typeof setTimeout> | null;
  restartTimestamps: number[];
  failed: boolean;
  detachLogger: (() => void) | null;
  /** Watchdog for the initial connect — see CONNECT_TIMEOUT_MS. */
  connectTimer: ReturnType<typeof setTimeout> | null;
  connectAttempts: number;
  /** sess-id from the o= line of the peer's last SDP. A DIFFERENT sess-id in a
   *  later offer means the peer rebuilt its RTCPeerConnection (crash-rejoin) —
   *  the offer must go to a fresh pc on our side too. */
  remoteSessionId: string | null;
}

/** sess-id from an SDP "o=" line (RFC 4566: o=<user> <sess-id> <sess-version> …). */
function sdpSessionId(sdp: string | null | undefined): string | null {
  const m = /^o=\S+ (\S+) \S+/m.exec(sdp ?? "");
  return m ? m[1] : null;
}

export interface RoomPeer {
  peerId: PeerId;
  name: string;
  isGuest: boolean;
  /** Single stream: audio + one video (camera or screen — see mediaState). */
  stream: MediaStream | null;
  mediaState: MediaState;
  connectionState: string;
  failed: boolean;
}

export interface UseMeetingRoom {
  status: RoomStatus;
  error: string | null;
  me: PeerInfo | null;
  peers: RoomPeer[];
  contract: JoinContract | null;
  localCameraStream: MediaStream | null;
  localScreenStream: MediaStream | null;
  mediaState: MediaState;
  /** Camera was auto-disabled because the room was already crowded. */
  cameraAutoDisabled: boolean;
  join: () => Promise<void>;
  leave: () => void;
  toggleMic: () => void;
  toggleCamera: () => Promise<void>;
  /** Explicit "Udostępnij" — (re)request camera + mic permission. */
  requestDevices: () => Promise<boolean>;
  startScreenShare: () => Promise<void>;
  stopScreenShare: () => void;
  switchMic: (deviceId: string) => Promise<void>;
  switchCamera: (deviceId: string) => Promise<void>;
  /** Dismiss the current transient error. */
  dismissError: () => void;
  /** Record a UI-side diagnostic event (playback/sink problems) in the session log. */
  logEvent: (type: string, payload?: Record<string, unknown>) => void;
}

interface Options {
  occurrenceId: string;
  transport: RoomTransport;
  devicePrefs: DevicePrefs;
  initialAudio?: boolean;
  initialVideo?: boolean;
  /** Fired when a `note_changed` event arrives on the room channel — lets a
   *  guest (who has no authenticated notes subscription) refresh notes live. */
  onNoteChanged?: () => void;
  /** Fired when the organizer toggles guest-notes visibility for this meeting. */
  onNotesVisibility?: (enabled: boolean) => void;
}

/**
 * Full-mesh multi-party room over the backend signal relay + Centrifugo room
 * channel. One RTCPeerConnection per remote peer with textbook PERFECT
 * NEGOTIATION: `polite = myPeerId > peerId` (plain string compare —
 * deterministic and symmetric), so renegotiations (camera on/off, screen
 * share, ICE restarts) never glare. Camera vs screen discrimination rides on
 * MediaStream ids announced via the `track-meta` broadcast — stream ids are
 * stable across renegotiations, unlike transceiver mids.
 *
 * The 1:1 screen-share hook (useScreenShare) is intentionally NOT built on
 * this — fixed roles + consent UX there; only `features/rtc/` is shared.
 */
export function useMeetingRoom(options: Options): UseMeetingRoom {
  const { occurrenceId, transport } = options;

  const [status, setStatus] = useState<RoomStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const [me, setMe] = useState<PeerInfo | null>(null);
  const [contract, setContract] = useState<JoinContract | null>(null);
  const [peers, setPeers] = useState<RoomPeer[]>([]);
  const [localCameraStream, setLocalCameraStream] = useState<MediaStream | null>(null);
  const [localScreenStream, setLocalScreenStream] = useState<MediaStream | null>(null);
  const [mediaState, setMediaState] = useState<MediaState>({
    audio: true,
    video: true,
    screen: false,
  });
  const [cameraAutoDisabled, setCameraAutoDisabled] = useState(false);

  const statusRef = useRef<RoomStatus>("idle");
  const meRef = useRef<PeerInfo | null>(null);
  const onNoteChangedRef = useRef(options.onNoteChanged);
  onNoteChangedRef.current = options.onNoteChanged;
  const onNotesVisibilityRef = useRef(options.onNotesVisibility);
  onNotesVisibilityRef.current = options.onNotesVisibility;
  const entriesRef = useRef<Map<PeerId, PeerEntry>>(new Map());
  const cameraStreamRef = useRef<MediaStream | null>(null);
  const screenStreamRef = useRef<MediaStream | null>(null);
  const mediaStateRef = useRef<MediaState>({ audio: true, video: true, screen: false });
  const loggerRef = useRef<RtcLogger | null>(null);
  const unsubscribeRef = useRef<(() => void) | null>(null);
  const prefsRef = useRef<DevicePrefs>(options.devicePrefs);
  prefsRef.current = options.devicePrefs;
  // media-state signals that raced ahead of the peer's entry (directed sends
  // land during our getUserMedia await) — applied when the entry is created,
  // so a muted peer never renders with the optimistic audio:true default.
  const pendingMediaStateRef = useRef<Map<PeerId, MediaState>>(new Map());
  // Last time we honored a "renegotiate" for a peer (survives entry recreation).
  const renegotiateGuardRef = useRef<Map<PeerId, number>>(new Map());
  // Same, for rebuilds triggered by a repeated participant-joined announce.
  const rebuildGuardRef = useRef<Map<PeerId, number>>(new Map());
  // Room events are handled strictly one at a time — handleRoomEvent awaits
  // setRemoteDescription/sendSignal, and two interleaved descriptions for the
  // same pc throw InvalidStateError with no recovery.
  const eventQueueRef = useRef<Promise<void>>(Promise.resolve());
  // A leave that must wait for an in-flight join to finish. Set by the unmount
  // cleanup and by pagehide; CLEARED when the lifecycle effect re-runs, which
  // is what distinguishes a real unmount from StrictMode's dev
  // mount→cleanup→mount cycle (there, setup runs again in the same commit,
  // before any await inside join() resolves). Aborting outright instead left
  // the dev room stuck at "idle" forever, because nothing re-joins.
  const pendingLeaveRef = useRef<null | "unmount" | "page-hide">(null);
  // ICE servers for entries created mid-meeting: join-time snapshot, refreshed
  // via transport.freshIce once it ages past ICE_REFRESH_AFTER_MS (TURN
  // credentials are short-lived HMAC — a peer connection built hours in with
  // the join-time set has no working relay).
  const iceRef = useRef<{ servers: RTCIceServer[]; fetchedAt: number }>({
    servers: [],
    fetchedAt: 0,
  });
  const transportRef = useRef(transport);
  transportRef.current = transport;
  const currentIceRef = useRef<() => Promise<RTCIceServer[]>>(async () => []);
  currentIceRef.current = async () => {
    const age = Date.now() - iceRef.current.fetchedAt;
    // Never trust the cached set past the server's own credential TTL — TURN
    // usernames are time-limited HMACs, and a peer connection built with dead
    // credentials has no relay at all on a strict network.
    const ttlMs = (contractRef.current?.ice.ttl_seconds ?? 0) * 800; // 80% of TTL
    const horizon = ttlMs > 0 ? Math.min(ICE_REFRESH_AFTER_MS, ttlMs) : ICE_REFRESH_AFTER_MS;
    if (iceRef.current.servers.length && age < horizon) {
      return iceRef.current.servers;
    }
    try {
      // Room events are handled on one serialized queue — a hung refresh here
      // would stall ALL signaling, so cap it hard and fall back to stale.
      const fresh = await Promise.race([
        transportRef.current.freshIce(),
        new Promise<null>((resolve) => setTimeout(() => resolve(null), 3_000)),
      ]);
      if (fresh && fresh.length) {
        iceRef.current = { servers: fresh, fetchedAt: Date.now() };
        return fresh;
      }
    } catch {
      /* stale set below still beats none */
    }
    return iceRef.current.servers.length
      ? iceRef.current.servers
      : (contractRef.current?.ice.ice_servers ?? []);
  };

  const setStatusBoth = useCallback((s: RoomStatus) => {
    statusRef.current = s;
    setStatus(s);
  }, []);

  const setMediaStateBoth = useCallback((s: MediaState) => {
    mediaStateRef.current = s;
    setMediaState(s);
  }, []);

  // ── UI projection ──────────────────────────────────────────────────────────

  const syncPeers = useCallback(() => {
    const list: RoomPeer[] = [];
    for (const entry of entriesRef.current.values()) {
      // One stream per peer (audio + one video). Whether that video is a camera
      // or a screen is told by the peer's broadcast media_state, not by parsing
      // the stream — so there is no id-matching race.
      const hasTracks = entry.remoteStream.getTracks().length > 0;
      list.push({
        peerId: entry.info.peer_id,
        name: entry.info.name,
        isGuest: entry.info.is_guest,
        stream: hasTracks ? entry.remoteStream : null,
        mediaState: entry.mediaState,
        connectionState: entry.connectionState,
        failed: entry.failed,
      });
    }
    list.sort((a, b) => a.name.localeCompare(b.name, "pl"));
    setPeers(list);
  }, []);

  // ── quality management ─────────────────────────────────────────────────────

  const applyVideoParameters = useCallback(() => {
    const count = entriesRef.current.size + 1;
    const screenActive = mediaStateRef.current.screen;
    const tier = cameraTierFor(count, screenActive);
    const apply = (
      sender: RTCRtpSender,
      spec: { maxBitrate: number; scaleResolutionDownBy?: number; maxFramerate?: number },
    ) => {
      if (!sender.track) return;
      const params = sender.getParameters();
      if (!params.encodings || params.encodings.length === 0) {
        params.encodings = [{}];
      }
      const enc = params.encodings[0];
      enc.maxBitrate = spec.maxBitrate;
      if (spec.scaleResolutionDownBy) enc.scaleResolutionDownBy = spec.scaleResolutionDownBy;
      else delete enc.scaleResolutionDownBy;
      if (spec.maxFramerate) enc.maxFramerate = spec.maxFramerate;
      else delete enc.maxFramerate;
      sender.setParameters(params).catch(() => {
        /* transient (mid-negotiation) — next recompute will land */
      });
    };
    // Single video sender per pc: it carries the screen (high bitrate) when
    // I'm sharing, otherwise the camera at the crowd-scaled tier.
    const spec = screenActive ? { maxBitrate: SCREEN_MAX_BITRATE } : tier;
    for (const entry of entriesRef.current.values()) {
      if (entry.transceivers) apply(entry.transceivers.video.sender, spec);
    }
    loggerRef.current?.log("quality_tier", {
      participants: count,
      screen: screenActive,
      ...tier,
    });
  }, []);

  // ── local video channel (camera OR screen — one at a time) ──────────────────

  // The single local video track sent to peers: the screen when sharing, else
  // the camera (kept even when disabled — track.enabled gates the picture).
  const activeVideoTrack = useCallback((): MediaStreamTrack | null => {
    if (mediaStateRef.current.screen && screenStreamRef.current) {
      return screenStreamRef.current.getVideoTracks()[0] ?? null;
    }
    return cameraStreamRef.current?.getVideoTracks()[0] ?? null;
  }, []);
  // Ref so createEntry (a stable callback) always reads the latest.
  const activeVideoTrackRef = useRef(activeVideoTrack);
  activeVideoTrackRef.current = activeVideoTrack;

  // Attach our current local audio + active video to a peer's senders. Called
  // for the initiator at pc creation, and for the responder once the browser
  // has created transceivers from the first offer.
  const attachLocalToEntry = useCallback((entry: PeerEntry) => {
    if (!entry.transceivers) return;
    const audio = cameraStreamRef.current?.getAudioTracks()[0] ?? null;
    if (audio) void entry.transceivers.audio.sender.replaceTrack(audio);
    const video = activeVideoTrackRef.current();
    if (video) void entry.transceivers.video.sender.replaceTrack(video);
    // audio:false here on an entry that never gets another attach = we are
    // sending silence to this peer while our mic icon shows green.
    loggerRef.current?.log(
      "local_tracks_attached",
      { audio: !!audio, video: !!video },
      entry.info.peer_id,
    );
  }, []);

  // Point every peer's video sender at the current active track. replaceTrack
  // needs no renegotiation, so camera↔screen swaps are instant and generate no
  // signaling storm.
  const updateVideoSender = useCallback(() => {
    const track = activeVideoTrack();
    for (const entry of entriesRef.current.values()) {
      if (entry.transceivers) void entry.transceivers.video.sender.replaceTrack(track);
    }
    applyVideoParameters();
  }, [activeVideoTrack, applyVideoParameters]);

  // ── outgoing metadata ──────────────────────────────────────────────────────

  const broadcastMediaState = useCallback(
    (to: PeerId | null = null, attempt = 0) => {
      void transport
        .sendSignal({ to, type: "media-state", payload: mediaStateRef.current })
        .catch((err: Error & { status?: number }) => {
          loggerRef.current?.log("signal_send_error", {
            signal: "media-state",
            status: err.status,
            error: String(err),
          });
          // The directed send to a late joiner lands exactly in the join/leave
          // window where the relay 409s; losing it leaves our mic icon wrong on
          // their side for the whole meeting. One delayed retry covers it.
          if (to && attempt === 0 && statusRef.current === "active") {
            setTimeout(() => {
              if (statusRef.current === "active") broadcastMediaState(to, 1);
            }, 2_000);
          }
        });
    },
    [transport],
  );

  // ── peer lifecycle ─────────────────────────────────────────────────────────

  const closeEntry = useCallback(
    (peerId: PeerId) => {
      const entry = entriesRef.current.get(peerId);
      if (!entry) return;
      if (entry.graceTimer) clearTimeout(entry.graceTimer);
      if (entry.connectTimer) clearTimeout(entry.connectTimer);
      entry.detachLogger?.();
      try {
        entry.pc.close();
      } catch {
        /* ignore */
      }
      entriesRef.current.delete(peerId);
      syncPeers();
      applyVideoParameters();
    },
    [applyVideoParameters, syncPeers],
  );

  const attemptIceRestart = useCallback((entry: PeerEntry, reason: string) => {
    const now = Date.now();
    entry.restartTimestamps = entry.restartTimestamps.filter(
      (t) => now - t < ICE_RESTART_WINDOW_MS,
    );
    if (entry.restartTimestamps.length >= ICE_RESTART_MAX) {
      loggerRef.current?.log(
        "ice_restart_gave_up",
        { reason },
        entry.info.peer_id,
      );
      // Only this tile degrades — the rest of the meeting lives on.
      entry.failed = true;
      syncPeers();
      return;
    }
    entry.restartTimestamps.push(now);
    loggerRef.current?.log("ice_restart", { reason }, entry.info.peer_id);
    if (!entry.isInitiator) {
      // restartIce() only queues a negotiationneeded event, and the responder
      // never offers — on this side it was a silent no-op that burned the
      // restart budget doing nothing. Ask the initiator to rebuild instead.
      void transport
        .sendSignal({
          to: entry.info.peer_id,
          type: "renegotiate",
          payload: { reason },
        })
        .catch((err: Error & { status?: number }) => {
          loggerRef.current?.log(
            "signal_send_error",
            { signal: "renegotiate", status: err.status, error: String(err) },
            entry.info.peer_id,
          );
        });
      return;
    }
    // Perfect negotiation turns the restart into an ordinary offer exchange.
    entry.pc.restartIce();
  }, [syncPeers, transport]);

  const createEntry = useCallback(
    (info: PeerInfo, ice: RTCIceServer[]) => {
      const myId = meRef.current?.peer_id ?? "";
      if (info.peer_id === myId || entriesRef.current.has(info.peer_id)) return;
      // Deterministic initiator per pair: exactly one side offers → no glare.
      const isInitiator = myId > info.peer_id;
      const pc = new RTCPeerConnection({ iceServers: ice });
      const entry: PeerEntry = {
        info,
        pc,
        // The responder (non-initiator) is the polite peer for the perfect-
        // negotiation collision rules.
        polite: !isInitiator,
        isInitiator,
        makingOffer: false,
        ignoreOffer: false,
        remoteDescSet: false,
        pendingIce: [],
        transceivers: null,
        remoteStream: new MediaStream(),
        // A media-state signal may have raced ahead of this entry — honor it
        // instead of the optimistic default (which rendered muted peers as
        // unmuted: "his mic icon is green but we hear nothing").
        mediaState: pendingMediaStateRef.current.get(info.peer_id) ?? {
          audio: true,
          video: true,
          screen: false,
        },
        connectionState: "new",
        graceTimer: null,
        restartTimestamps: [],
        failed: false,
        detachLogger: loggerRef.current?.attachPc(pc, info.peer_id) ?? null,
        connectTimer: null,
        connectAttempts: 0,
        remoteSessionId: null,
      };
      pendingMediaStateRef.current.delete(info.peer_id);
      entriesRef.current.set(info.peer_id, entry);

      // Initial-connect watchdog. The join announce is published before the
      // joiner's first channel subscribe, so the initiator's instant offer can
      // be lost with no replay — and nothing else ever re-offers. Whichever
      // side notices the stall repairs it: the initiator re-sends its offer
      // (or ICE-restarts), the responder asks the initiator to start over.
      let checkingGraceUsed = false;
      const armConnectWatchdog = () => {
        if (entry.connectTimer) clearTimeout(entry.connectTimer);
        entry.connectTimer = setTimeout(() => {
          entry.connectTimer = null;
          if (entriesRef.current.get(info.peer_id) !== entry) return;
          const st = pc.iceConnectionState;
          if (st === "connected" || st === "completed") return;
          if (entry.connectAttempts >= CONNECT_ATTEMPTS_MAX) {
            loggerRef.current?.log(
              "connect_watchdog_gave_up",
              { state: st },
              info.peer_id,
            );
            entry.failed = true;
            syncPeers();
            return;
          }
          // ICE candidates are still being paired ("checking"): the signaling
          // clearly got through, so give a slow TURN allocation ONE more window
          // instead of throwing the progress away with a restart.
          const stillNegotiating = st === "checking" && !checkingGraceUsed;
          if (stillNegotiating) checkingGraceUsed = true;
          if (!stillNegotiating) {
            entry.connectAttempts += 1;
            loggerRef.current?.log(
              "connect_watchdog",
              {
                attempt: entry.connectAttempts,
                state: st,
                signaling: pc.signalingState,
              },
              info.peer_id,
            );
            if (entry.isInitiator) {
              if (pc.signalingState === "have-local-offer" && pc.localDescription) {
                // Our first offer likely never arrived (join/subscribe race) —
                // send the exact same one again.
                void transport
                  .sendSignal({
                    to: info.peer_id,
                    type: "offer",
                    payload: pc.localDescription,
                  })
                  .catch((err: Error & { status?: number }) => {
                    loggerRef.current?.log(
                      "signal_send_error",
                      { signal: "offer", status: err.status, error: String(err) },
                      info.peer_id,
                    );
                  });
              } else {
                pc.restartIce();
              }
            } else {
              // The responder's ONLY recovery tool — whether the offer never
              // arrived or ICE is stuck after answering.
              void transport
                .sendSignal({ to: info.peer_id, type: "renegotiate", payload: {} })
                .catch((err: Error & { status?: number }) => {
                  loggerRef.current?.log(
                    "signal_send_error",
                    { signal: "renegotiate", status: err.status, error: String(err) },
                    info.peer_id,
                  );
                });
            }
          }
          armConnectWatchdog();
        }, CONNECT_TIMEOUT_MS);
      };
      armConnectWatchdog();

      pc.onnegotiationneeded = async () => {
        // Only the initiator ever offers.
        if (!entry.isInitiator) return;
        try {
          entry.makingOffer = true;
          await pc.setLocalDescription();
          await transport.sendSignal({
            to: info.peer_id,
            type: "offer",
            payload: pc.localDescription,
          });
        } catch (err) {
          loggerRef.current?.log(
            "negotiation_error",
            { error: String(err) },
            info.peer_id,
          );
        } finally {
          entry.makingOffer = false;
        }
      };
      pc.onicecandidate = (e) => {
        if (e.candidate) {
          void transport
            .sendSignal({ to: info.peer_id, type: "ice", payload: e.candidate.toJSON() })
            .catch((err: Error & { status?: number }) => {
              // The relay 409s when the target is not in the roster (join/leave
              // window) — a silently dropped candidate is a lost connectivity
              // option, so record it.
              loggerRef.current?.log(
                "signal_send_error",
                { signal: "ice", status: err.status, error: String(err) },
                info.peer_id,
              );
            });
        }
      };
      pc.ontrack = (e) => {
        // One stream per peer; the single video track is camera or screen per
        // the peer's broadcast media_state.
        if (!entry.remoteStream.getTracks().includes(e.track)) {
          entry.remoteStream.addTrack(e.track);
        }
        loggerRef.current?.log(
          "track_received",
          { kind: e.track.kind, muted: e.track.muted },
          info.peer_id,
        );
        // unmute = RTP actually started flowing for this track; a track that
        // never unmutes means the sender attached nothing (or muted at source).
        e.track.onunmute = () => {
          loggerRef.current?.log(
            "track_unmuted",
            { kind: e.track.kind },
            info.peer_id,
          );
          syncPeers();
        };
        e.track.onmute = () => {
          loggerRef.current?.log(
            "track_muted",
            { kind: e.track.kind },
            info.peer_id,
          );
          syncPeers();
        };
        e.track.onended = () => syncPeers();
        syncPeers();
      };
      pc.oniceconnectionstatechange = () => {
        const st = pc.iceConnectionState;
        entry.connectionState = st;
        if (st === "connected" || st === "completed") {
          if (entry.graceTimer) {
            clearTimeout(entry.graceTimer);
            entry.graceTimer = null;
          }
          if (entry.connectTimer) {
            clearTimeout(entry.connectTimer);
            entry.connectTimer = null;
          }
          entry.connectAttempts = 0;
          entry.failed = false;
        } else if (st === "disconnected") {
          if (!entry.graceTimer) {
            entry.graceTimer = setTimeout(() => {
              entry.graceTimer = null;
              const cur = pc.iceConnectionState;
              if (cur === "disconnected" || cur === "failed") {
                attemptIceRestart(entry, "disconnected-grace");
              }
            }, DISCONNECTED_GRACE_MS);
          }
        } else if (st === "failed") {
          if (entry.graceTimer) {
            clearTimeout(entry.graceTimer);
            entry.graceTimer = null;
          }
          attemptIceRestart(entry, "failed");
        }
        syncPeers();
      };

      if (isInitiator) {
        // Initiator creates the two transceivers (audio + one video), attaches
        // local media, and offers (onnegotiationneeded fires once).
        const audioTx = pc.addTransceiver("audio", { direction: "sendrecv" });
        const videoTx = pc.addTransceiver("video", { direction: "sendrecv" });
        entry.transceivers = { audio: audioTx, video: videoTx };
        attachLocalToEntry(entry);
      }
      // Responder creates NO transceivers — the browser will create matching
      // ones from the initiator's offer, and we attach local media in
      // handleDescription once they exist.

      syncPeers();
      applyVideoParameters();
    },
    [applyVideoParameters, attachLocalToEntry, attemptIceRestart, syncPeers, transport],
  );

  // ── inbound events ─────────────────────────────────────────────────────────

  const handleDescription = useCallback(
    async (entry: PeerEntry, description: RTCSessionDescriptionInit) => {
      const pc = entry.pc;
      const offerCollision =
        description.type === "offer" &&
        (entry.makingOffer || pc.signalingState !== "stable");
      entry.ignoreOffer = !entry.polite && offerCollision;
      if (entry.ignoreOffer) return;
      await pc.setRemoteDescription(description);
      entry.remoteDescSet = true;
      entry.remoteSessionId = sdpSessionId(description.sdp) ?? entry.remoteSessionId;
      // Responder: the browser just created transceivers from the offer. Grab
      // them (by kind) and attach our local media BEFORE answering, so our
      // tracks are included in the answer.
      if (description.type === "offer" && !entry.transceivers) {
        const txs = pc.getTransceivers();
        const audio = txs.find(
          (t) => (t.receiver.track?.kind ?? t.sender.track?.kind) === "audio",
        );
        const video = txs.find(
          (t) => (t.receiver.track?.kind ?? t.sender.track?.kind) === "video",
        );
        if (audio && video) {
          // Transceivers auto-created from a remote offer default to RECVONLY —
          // the responder would never send its own audio/camera/screen. Force
          // sendrecv so our media flows back (this was the one-directional
          // screen-share bug: whoever ended up the responder couldn't send).
          try {
            audio.direction = "sendrecv";
            video.direction = "sendrecv";
          } catch {
            /* older browsers ignore direction assignment */
          }
          entry.transceivers = { audio, video };
          attachLocalToEntry(entry);
        }
      }
      for (const cand of entry.pendingIce.splice(0)) {
        try {
          await pc.addIceCandidate(new RTCIceCandidate(cand));
        } catch {
          /* late/duplicate */
        }
      }
      if (description.type === "offer") {
        await pc.setLocalDescription();
        // NOT awaited: this runs on the single event queue, and a slow/hung
        // POST here would stall signaling for every other peer. The local
        // description is already set, so delivery is all that is pending.
        void transport
          .sendSignal({
            to: entry.info.peer_id,
            type: "answer",
            payload: pc.localDescription,
          })
          .catch((err: Error & { status?: number }) => {
            loggerRef.current?.log(
              "signal_send_error",
              { signal: "answer", status: err.status, error: String(err) },
              entry.info.peer_id,
            );
          });
        applyVideoParameters();
      }
    },
    [applyVideoParameters, attachLocalToEntry, transport],
  );

  const handleRoomEvent = useCallback(
    async (event: RoomEvent) => {
      // Notes change on the room channel — surface it even for a guest, who has
      // no authenticated notes subscription (this is their live path).
      if ((event as { type?: string }).type === "note_changed") {
        onNoteChangedRef.current?.();
        return;
      }
      if ((event as { type?: string }).type === "notes_visibility") {
        onNotesVisibilityRef.current?.(
          (event as { enabled?: boolean }).enabled === true,
        );
        return;
      }
      const myId = meRef.current?.peer_id;
      if (!myId || statusRef.current === "ended") return;

      if (event.type === "participant-joined") {
        if (event.peer.peer_id === myId) return;
        const existing = entriesRef.current.get(event.peer.peer_id);
        if (existing) {
          // The backend announces EVERY join, including a rejoin over a reused
          // attendance row (crash-rejoin). Only a BROKEN connection is rebuilt:
          // "new"/"checking" is a handshake in progress (TURN can take seconds),
          // and tearing that down on every announce turned a flapping network
          // into a rebuild storm that never converged.
          const broken =
            existing.failed ||
            existing.connectionState === "failed" ||
            existing.connectionState === "disconnected" ||
            existing.connectionState === "closed";
          const lastRebuild = rebuildGuardRef.current.get(event.peer.peer_id) ?? 0;
          if (broken && Date.now() - lastRebuild >= RENEGOTIATE_MIN_INTERVAL_MS) {
            rebuildGuardRef.current.set(event.peer.peer_id, Date.now());
            loggerRef.current?.log(
              "peer_rejoined_rebuild",
              { state: existing.connectionState },
              event.peer.peer_id,
            );
            closeEntry(event.peer.peer_id);
            createEntry(event.peer, await currentIceRef.current());
          }
          broadcastMediaState(event.peer.peer_id);
          return;
        }
        createEntry(event.peer, await currentIceRef.current());
        // Late joiner needs our current mic/cam/screen flags immediately.
        broadcastMediaState(event.peer.peer_id);
        return;
      }
      if (event.type === "participant-left") {
        closeEntry(event.peer_id);
        return;
      }
      if (event.type === "ended") {
        loggerRef.current?.log("room_ended_by_organizer");
        teardownRef.current("remote-end");
        setStatusBoth("ended");
        return;
      }

      // Directed / broadcast signaling.
      if (event.from.peer_id === myId) return;
      if (event.to && event.to !== myId) return;
      const entry = entriesRef.current.get(event.from.peer_id);

      switch (event.type) {
        case "offer": {
          let target = entry;
          const description = event.payload as RTCSessionDescriptionInit;
          if (target?.remoteSessionId) {
            const incoming = sdpSessionId(description.sdp);
            if (incoming && incoming !== target.remoteSessionId) {
              // Different o= sess-id ⇒ the peer rebuilt its RTCPeerConnection
              // (rejoin after crash, or a renegotiate-triggered rebuild). Our
              // old pc would reject the new DTLS/ICE credentials — start fresh.
              loggerRef.current?.log(
                "peer_new_session_rebuild",
                {},
                event.from.peer_id,
              );
              closeEntry(event.from.peer_id);
              target = undefined;
            }
          }
          if (!target) {
            // Peer we don't know yet (recovery edge / fresh session) — create
            // lazily and let the offer negotiate onto the new pc.
            createEntry(
              { peer_id: event.from.peer_id, name: event.from.name, is_guest: event.from.peer_id.startsWith("guest:") },
              await currentIceRef.current(),
            );
            target = entriesRef.current.get(event.from.peer_id);
          }
          if (!target) return;
          try {
            await handleDescription(target, description);
          } catch (err) {
            loggerRef.current?.log("offer_error", { error: String(err) }, event.from.peer_id);
          }
          break;
        }
        case "renegotiate": {
          // The responder side of this pair asked us to start over (it cannot
          // offer itself). Full rebuild: fresh pc, fresh offer — also covers
          // its crash-rejoin, where our existing pc is bound to a dead session.
          const initiatorForPeer = myId > event.from.peer_id;
          if (!initiatorForPeer) return;
          if (
            entry &&
            (entry.connectionState === "connected" ||
              entry.connectionState === "completed")
          ) {
            // Their watchdog fired just as ICE completed — tearing a working
            // connection down here would drop live media for both of us.
            loggerRef.current?.log("renegotiate_ignored_healthy", {}, event.from.peer_id);
            return;
          }
          const last = renegotiateGuardRef.current.get(event.from.peer_id) ?? 0;
          if (Date.now() - last < RENEGOTIATE_MIN_INTERVAL_MS) return;
          renegotiateGuardRef.current.set(event.from.peer_id, Date.now());
          loggerRef.current?.log(
            "renegotiate_honored",
            { reason: (event.payload as { reason?: string })?.reason, known: !!entry },
            event.from.peer_id,
          );
          // No entry at all = we never learned they joined (their announce was
          // lost). Their renegotiate is then the ONLY way we ever connect, so
          // create the peer from the signal itself.
          const info = entry?.info ?? {
            peer_id: event.from.peer_id,
            name: event.from.name,
            is_guest: event.from.peer_id.startsWith("guest:"),
          };
          if (entry) closeEntry(event.from.peer_id);
          createEntry(info, await currentIceRef.current());
          broadcastMediaState(event.from.peer_id);
          break;
        }
        case "answer": {
          if (!entry) return;
          try {
            await handleDescription(entry, event.payload as RTCSessionDescriptionInit);
          } catch (err) {
            loggerRef.current?.log("answer_error", { error: String(err) }, event.from.peer_id);
          }
          break;
        }
        case "ice": {
          if (!entry) return;
          const cand = event.payload as RTCIceCandidateInit;
          if (!entry.remoteDescSet) {
            entry.pendingIce.push(cand);
          } else {
            try {
              await entry.pc.addIceCandidate(new RTCIceCandidate(cand));
            } catch (err) {
              if (!entry.ignoreOffer) {
                loggerRef.current?.log("ice_add_error", { error: String(err) }, event.from.peer_id);
              }
            }
          }
          break;
        }
        case "track-meta":
          // Camera/screen routing is transceiver-based now; nothing to do.
          break;
        case "media-state": {
          const parsed: MediaState = {
            audio: true,
            video: true,
            screen: false,
            ...(event.payload as Partial<MediaState>),
          };
          if (!entry) {
            // Raced ahead of the entry (their directed send lands during our
            // getUserMedia await) — keep it for createEntry.
            pendingMediaStateRef.current.set(event.from.peer_id, parsed);
            return;
          }
          entry.mediaState = parsed;
          // Persisted so a "we heard nothing" report can distinguish a peer
          // whose mic was simply off from a broken media path.
          loggerRef.current?.log(
            "peer_media_state",
            { ...entry.mediaState },
            event.from.peer_id,
          );
          syncPeers();
          break;
        }
      }
    },
    [
      broadcastMediaState,
      closeEntry,
      createEntry,
      handleDescription,
      setStatusBoth,
      syncPeers,
    ],
  );

  const handlerRef = useRef(handleRoomEvent);
  handlerRef.current = handleRoomEvent;
  const contractRef = useRef<JoinContract | null>(null);

  // ── roster resync ──────────────────────────────────────────────────────────
  // After a re-subscribe the room channel's history may not cover our absence:
  // joins/leaves we missed are gone for good, leaving ghost tiles and unknown
  // peers. Re-join (idempotent server-side; re-announces us so peers re-check
  // their connection to us) and reconcile entries against the fresh roster.
  const resyncRef = useRef<() => Promise<void>>(async () => {});
  resyncRef.current = async () => {
    if (statusRef.current !== "active") return;
    try {
      const fresh = await transportRef.current.join();
      contractRef.current = fresh;
      iceRef.current = { servers: fresh.ice.ice_servers, fetchedAt: Date.now() };
      const myId = meRef.current?.peer_id;
      const alive = new Set(fresh.participants.map((p) => p.peer_id));
      for (const id of [...entriesRef.current.keys()]) {
        if (!alive.has(id)) closeEntry(id);
      }
      for (const p of fresh.participants) {
        if (p.peer_id !== myId && !entriesRef.current.has(p.peer_id)) {
          createEntry(p, fresh.ice.ice_servers);
        }
      }
      broadcastMediaState();
      loggerRef.current?.log("roster_resync", {
        participants: fresh.participants.length,
      });
    } catch (err) {
      loggerRef.current?.log("roster_resync_error", { error: String(err) });
    }
  };

  // ── teardown ───────────────────────────────────────────────────────────────

  const teardown = useCallback(
    (initiator: string) => {
      loggerRef.current?.log("teardown", { initiator });
      for (const peerId of [...entriesRef.current.keys()]) {
        const entry = entriesRef.current.get(peerId);
        if (entry?.graceTimer) clearTimeout(entry.graceTimer);
        if (entry?.connectTimer) clearTimeout(entry.connectTimer);
        entry?.detachLogger?.();
        try {
          entry?.pc.close();
        } catch {
          /* ignore */
        }
        entriesRef.current.delete(peerId);
      }
      unsubscribeRef.current?.();
      unsubscribeRef.current = null;
      pendingMediaStateRef.current.clear();
      renegotiateGuardRef.current.clear();
      rebuildGuardRef.current.clear();
      // A rejoin must not inherit a chain that is still waiting on a request
      // from the dead session — that stalled the new session's signaling too.
      eventQueueRef.current = Promise.resolve();
      cameraStreamRef.current?.getTracks().forEach((t) => t.stop());
      cameraStreamRef.current = null;
      screenStreamRef.current?.getTracks().forEach((t) => t.stop());
      screenStreamRef.current = null;
      setLocalCameraStream(null);
      setLocalScreenStream(null);
      setPeers([]);
      loggerRef.current?.dispose();
      loggerRef.current = null;
    },
    [],
  );
  const teardownRef = useRef(teardown);
  teardownRef.current = teardown;

  // ── public actions ─────────────────────────────────────────────────────────

  /**
   * getUserMedia for ONE kind, with diagnostics. A saved device preference is
   * requested with `deviceId: exact` — if that device is gone (undocked
   * headset, rotated device id), the call rejects and the meeting would start
   * silently without a mic/camera. Retry once with the default device, and
   * persist every failure with its DOMException name (NotReadableError,
   * NotAllowedError, …) — these were previously swallowed whole.
   */
  const acquireLocalTrack = useCallback(
    async (
      kind: "audio" | "video",
    ): Promise<{ track: MediaStreamTrack | null; error: string | null }> => {
      const constraintsFor = (usePref: boolean) => {
        const prefs = usePref
          ? prefsRef.current
          : { ...prefsRef.current, micId: null, cameraId: null };
        return kind === "audio" ? audioConstraints(prefs) : videoConstraints(prefs);
      };
      const pickTrack = (media: MediaStream): MediaStreamTrack | null => {
        const track =
          kind === "audio" ? media.getAudioTracks()[0] : media.getVideoTracks()[0];
        for (const t of media.getTracks()) if (t !== track) t.stop();
        if (track && kind === "video") track.contentHint = "motion";
        return track ?? null;
      };
      const errName = (err: unknown) =>
        err instanceof DOMException ? err.name : String(err);
      const hasPref =
        kind === "audio" ? !!prefsRef.current.micId : !!prefsRef.current.cameraId;
      try {
        return {
          track: pickTrack(
            await navigator.mediaDevices.getUserMedia(constraintsFor(true)),
          ),
          error: null,
        };
      } catch (err) {
        const name = errName(err);
        if (hasPref && (name === "OverconstrainedError" || name === "NotFoundError")) {
          loggerRef.current?.log("media_fallback", { kind, error: name });
          try {
            return {
              track: pickTrack(
                await navigator.mediaDevices.getUserMedia(constraintsFor(false)),
              ),
              error: null,
            };
          } catch (err2) {
            const name2 = errName(err2);
            loggerRef.current?.log("media_error", { kind, error: name2 });
            return { track: null, error: name2 };
          }
        }
        loggerRef.current?.log("media_error", { kind, error: name });
        return { track: null, error: name };
      }
    },
    [],
  );

  /** Polish, actionable explanation of a failed MIC acquisition — shown as the
   *  room banner so a participant is never silently mute (2026-08-04, round 2:
   *  a browser-level mic block sank the meeting with zero UI feedback). */
  const micErrorMessage = (error: string): string => {
    switch (error) {
      case "NotAllowedError":
      case "PermissionDeniedError":
        return (
          "Przeglądarka blokuje dostęp do mikrofonu — inni Cię NIE słyszą. " +
          "Kliknij ikonę kłódki obok adresu strony, zezwól na mikrofon i " +
          "włącz go przyciskiem mikrofonu."
        );
      case "NotReadableError":
        return (
          "Mikrofon jest zajęty przez inny program (np. Teams/Zoom) — inni " +
          "Cię NIE słyszą. Zamknij tamten program i włącz mikrofon przyciskiem."
        );
      case "NotFoundError":
        return "Nie wykryto mikrofonu — inni Cię NIE słyszą. Podłącz mikrofon i włącz go przyciskiem.";
      default:
        return "Nie udało się uruchomić mikrofonu — inni Cię NIE słyszą.";
    }
  };

  // ── local track health ─────────────────────────────────────────────────────
  // A Bluetooth/USB headset dying mid-call ends its MediaStreamTrack; nothing
  // reacted before, so the app kept "sending" from a dead track behind a green
  // mic icon with every recovery path (toggle, re-request) fooled by the
  // track's mere presence. Watch every local track and auto-reacquire.
  const recoveringRef = useRef<{ audio: boolean; video: boolean }>({
    audio: false,
    video: false,
  });
  const recoverAttemptsRef = useRef<{ audio: number[]; video: number[] }>({
    audio: [],
    video: [],
  });
  const recoverLocalTrackRef = useRef<(kind: "audio" | "video") => Promise<void>>(
    async () => {},
  );
  const watchLocalTrack = useCallback(
    (track: MediaStreamTrack, kind: "audio" | "video") => {
      track.onended = () => {
        loggerRef.current?.log("local_track_ended", { kind });
        void recoverLocalTrackRef.current(kind);
      };
    },
    [],
  );
  recoverLocalTrackRef.current = async (kind: "audio" | "video") => {
    if (statusRef.current !== "active" && statusRef.current !== "joining") return;
    if (recoveringRef.current[kind]) return;
    // A device that dies again the instant it is reacquired would otherwise
    // spin (ended → reacquire → ended …) at full speed. Cap the burst.
    const now = Date.now();
    const attempts = recoverAttemptsRef.current[kind];
    const recent = attempts.filter((t) => now - t < RECOVER_WINDOW_MS);
    if (recent.length >= RECOVER_MAX_ATTEMPTS) {
      loggerRef.current?.log("local_track_recover_gave_up", { kind });
      recoverAttemptsRef.current[kind] = recent;
      return;
    }
    recent.push(now);
    recoverAttemptsRef.current[kind] = recent;
    recoveringRef.current[kind] = true;
    try {
      const { track, error } = await acquireLocalTrack(kind);
      if (!track) {
        loggerRef.current?.log("local_track_recover_failed", { kind, error });
        if (kind === "audio") {
          setError(micErrorMessage(error ?? ""));
          setMediaStateBoth({ ...mediaStateRef.current, audio: false });
        } else {
          setMediaStateBoth({ ...mediaStateRef.current, video: false });
        }
        broadcastMediaState();
        return;
      }
      watchLocalTrack(track, kind);
      track.enabled =
        kind === "audio" ? mediaStateRef.current.audio : mediaStateRef.current.video;
      const tracks = cameraStreamRef.current?.getTracks() ?? [];
      tracks.filter((t) => t.kind === kind).forEach((t) => t.stop());
      // NEW stream identity — tiles re-attach srcObject and useSpeaking re-wires
      // its analyser (both key off the stream object, not the tracks inside).
      const next = new MediaStream([
        ...tracks.filter((t) => t.kind !== kind),
        track,
      ]);
      cameraStreamRef.current = next;
      setLocalCameraStream(next);
      if (kind === "audio") {
        for (const entry of entriesRef.current.values()) {
          if (entry.transceivers) {
            entry.transceivers.audio.sender.replaceTrack(track).catch(() => {});
          }
        }
      } else if (!mediaStateRef.current.screen) {
        updateVideoSender();
      }
      loggerRef.current?.log("local_track_recovered", { kind });
    } finally {
      recoveringRef.current[kind] = false;
    }
  };

  /** The room went away (unmount / pagehide) while we were joining: finish the
   *  join cleanly, then undo it. Returns true when the caller must stop. */
  const settlePendingLeave = useCallback(
    (tracks: (MediaStreamTrack | null)[] = []) => {
      const pending = pendingLeaveRef.current;
      if (!pending) return false;
      tracks.forEach((t) => t?.stop());
      void transport.leave();
      teardownRef.current(`join-${pending}`);
      setStatusBoth(pending === "page-hide" ? "ended" : "idle");
      pendingLeaveRef.current = null;
      return true;
    },
    [setStatusBoth, transport],
  );

  const join = useCallback(async () => {
    if (statusRef.current !== "idle" && statusRef.current !== "error") return;
    setError(null);
    pendingLeaveRef.current = null;
    setStatusBoth("joining");
    try {
      const joined = await transport.join();
      if (settlePendingLeave()) return;
      contractRef.current = joined;
      setContract(joined);
      meRef.current = joined.me;
      setMe(joined.me);
      setIceServers(joined.ice.ice_servers, joined.ice.ttl_seconds);
      iceRef.current = { servers: joined.ice.ice_servers, fetchedAt: Date.now() };

      // Guests enter with a placeholder occurrenceId — the real one arrives in
      // the join contract and MUST be used (guest diagnostics are validated
      // against the invite's occurrence server-side).
      const sessionId =
        (joined.occurrence as { id?: string } | undefined)?.id ?? occurrenceId;
      loggerRef.current = createRtcLogger({
        kind: "meeting",
        sessionId,
        ...transport.loggerOverrides(),
      });
      loggerRef.current.log("room_join", {
        participants: joined.participants.length,
        has_turn: joined.ice.ice_servers.some((s) =>
          (Array.isArray(s.urls) ? s.urls : [s.urls]).some((u) =>
            String(u).startsWith("turn"),
          ),
        ),
      });

      // Subscribe BEFORE creating peers so no answer/ice gets lost. Events are
      // funneled through one promise chain: the handler awaits set*Description
      // and two interleaved descriptions for the same pc throw InvalidStateError.
      unsubscribeRef.current = await transport.subscribeRoom(
        joined,
        (e) => {
          eventQueueRef.current = eventQueueRef.current
            .then(() => handlerRef.current(e))
            .catch(() => {
              /* handler logs its own errors */
            });
        },
        () => void resyncRef.current(),
      );
      if (settlePendingLeave()) return;

      // Local media. Crowded room → camera starts disabled (encode budget).
      const others = joined.participants.filter(
        (p) => p.peer_id !== joined.me.peer_id,
      );
      const wantVideo =
        (options.initialVideo ?? true) && others.length + 1 < CAMERA_OFF_AT;
      setCameraAutoDisabled(
        (options.initialVideo ?? true) && others.length + 1 >= CAMERA_OFF_AT,
      );
      // Acquire audio and video INDEPENDENTLY (see useMediaDevices): a mic-only
      // machine must still get its mic prompt and join with sound. Neither a
      // missing device nor a denied permission blocks joining — a listen/watch-
      // only participant is valid, and devices can be enabled later.
      const audio = await acquireLocalTrack("audio");
      const audioTrack = audio.track;
      const videoTrack = wantVideo ? (await acquireLocalTrack("video")).track : null;
      if (settlePendingLeave([audioTrack, videoTrack])) return;
      if (audioTrack) watchLocalTrack(audioTrack, "audio");
      if (videoTrack) watchLocalTrack(videoTrack, "video");
      // A dead mic must be LOUD in the UI — the participant is about to talk
      // into the void. A missing camera is routine (many machines have none).
      if (audio.error) setError(micErrorMessage(audio.error));
      if (audioTrack || videoTrack) {
        const stream = new MediaStream();
        if (audioTrack) stream.addTrack(audioTrack);
        if (videoTrack) stream.addTrack(videoTrack);
        cameraStreamRef.current = stream;
        setLocalCameraStream(stream);
        const audioOn = (options.initialAudio ?? true) && !!audioTrack;
        if (audioTrack) audioTrack.enabled = audioOn;
        setMediaStateBoth({ audio: audioOn, video: !!videoTrack, screen: false });
      } else {
        cameraStreamRef.current = null;
        setLocalCameraStream(null);
        setMediaStateBoth({ audio: false, video: false, screen: false });
        loggerRef.current?.log("no_local_media");
      }
      loggerRef.current?.log("media_acquired", {
        audio: !!audioTrack,
        video: !!videoTrack,
        peers_during_acquire: entriesRef.current.size,
      });

      // CRITICAL: signaling was already live while getUserMedia was pending —
      // any peer entry created in that window (incoming offer, participant-
      // joined) went through attachLocalToEntry with no media and got EMPTY
      // senders. Nothing later re-attached them, so we transmitted silence for
      // the whole meeting while our own mic icon showed green (the 2026-08-04
      // failed-meeting incident). Re-point every existing sender at the tracks
      // we just acquired; entries created below get them via createEntry.
      for (const entry of entriesRef.current.values()) {
        attachLocalToEntry(entry);
      }

      for (const peer of others) {
        createEntry(peer, joined.ice.ice_servers);
      }
      broadcastMediaState();
      setStatusBoth("active");
      // The page may have gone away during the last awaits.
      settlePendingLeave();
    } catch (err) {
      const e = err as Error & { status?: number };
      loggerRef.current?.log("join_error", { error: String(e), status: e.status });
      teardownRef.current("join-error");
      setError(
        e.status === 409
          ? "Spotkanie jest pełne albo już zakończone."
          : "Nie udało się dołączyć do spotkania.",
      );
      setStatusBoth("error");
    }
  }, [
    acquireLocalTrack,
    attachLocalToEntry,
    broadcastMediaState,
    createEntry,
    occurrenceId,
    options.initialAudio,
    options.initialVideo,
    setMediaStateBoth,
    setStatusBoth,
    settlePendingLeave,
    transport,
    watchLocalTrack,
  ]);

  const leave = useCallback(() => {
    if (statusRef.current === "idle") return;
    void transport.leave();
    teardownRef.current("local-user");
    setStatusBoth("ended");
  }, [setStatusBoth, transport]);

  /**
   * Acquire a mic/camera track on demand and add it to every peer connection
   * (perfect negotiation renegotiates). Re-requestable: calling it after a
   * prior failure prompts the browser again — the path for "Udostępnij" when
   * the user joined without a device or previously denied. Returns true if a
   * track is now present (already-present counts as success).
   */
  const acquireAndAdd = useCallback(
    async (kind: "audio" | "video"): Promise<boolean> => {
      const already =
        kind === "audio"
          ? cameraStreamRef.current?.getAudioTracks()[0]
          : cameraStreamRef.current?.getVideoTracks()[0];
      // Presence is not health: a track whose device died is still "there" but
      // permanently ended — treating it as success left the user mute with no
      // way out short of rejoining.
      if (already && already.readyState === "live") return true;
      {
        const { track } = await acquireLocalTrack(kind);
        if (!track) return false;
        watchLocalTrack(track, kind);
        const keep = (cameraStreamRef.current?.getTracks() ?? []).filter(
          (t) => t.kind !== kind,
        );
        already?.stop();
        // NEW stream identity (never mutate in place): the self tile and its
        // "am I capturing?" analyser key off the stream object, so an in-place
        // swap left them wired to the dead track.
        const base = new MediaStream([...keep, track]);
        cameraStreamRef.current = base;
        setLocalCameraStream(base);
        if (kind === "audio") {
          // Attach to the audio sender on every pc — replaceTrack, no
          // renegotiation. (A responder whose transceivers aren't up yet picks
          // the track up via attachLocalToEntry on the first offer.)
          for (const entry of entriesRef.current.values()) {
            if (entry.transceivers) {
              void entry.transceivers.audio.sender.replaceTrack(track);
            }
          }
          setMediaStateBoth({ ...mediaStateRef.current, audio: true });
        } else {
          setCameraAutoDisabled(false);
          setMediaStateBoth({ ...mediaStateRef.current, video: true });
          // Only actually send the camera when not currently screen-sharing.
          if (!mediaStateRef.current.screen) updateVideoSender();
        }
        broadcastMediaState();
        return true;
      }
    },
    [
      acquireLocalTrack,
      broadcastMediaState,
      setMediaStateBoth,
      updateVideoSender,
      watchLocalTrack,
    ],
  );

  const toggleMic = useCallback(async () => {
    const existing = cameraStreamRef.current?.getAudioTracks()[0] ?? null;
    if (existing && existing.readyState === "live") {
      const next = !mediaStateRef.current.audio;
      existing.enabled = next;
      setMediaStateBoth({ ...mediaStateRef.current, audio: next });
      broadcastMediaState();
      return;
    }
    // No LIVE mic (joined listen-only, or the device died) → (re)acquire.
    if (!(await acquireAndAdd("audio"))) {
      setError("Nie udało się włączyć mikrofonu — brak urządzenia lub zgody.");
    }
  }, [acquireAndAdd, broadcastMediaState, setMediaStateBoth]);

  const toggleCamera = useCallback(async () => {
    const existing = cameraStreamRef.current?.getVideoTracks()[0] ?? null;
    if (existing && existing.readyState === "live") {
      const next = !mediaStateRef.current.video;
      existing.enabled = next;
      setMediaStateBoth({ ...mediaStateRef.current, video: next });
      broadcastMediaState();
      return;
    }
    // No LIVE camera (listen-only join, or the device died) → (re)acquire.
    if (!(await acquireAndAdd("video"))) {
      setError("Nie udało się włączyć kamery — brak urządzenia lub zgody.");
    }
  }, [acquireAndAdd, broadcastMediaState, setMediaStateBoth]);

  // Note: while screen-sharing, the video channel carries the screen; toggling
  // the camera flips the flag and the camera resumes automatically when the
  // screen share stops (updateVideoSender picks it up).

  /**
   * Explicit "Udostępnij kamerę i mikrofon" — requests BOTH, best-effort. Works
   * even after a previous failure (re-prompts). If the browser has hard-blocked
   * the site, both fail and we point the user at the address-bar lock icon.
   */
  const requestDevices = useCallback(async (): Promise<boolean> => {
    setError(null);
    const gotAudio = await acquireAndAdd("audio");
    const gotVideo = await acquireAndAdd("video");
    if (!gotAudio && !gotVideo) {
      setError(
        "Nie udało się uzyskać dostępu do kamery ani mikrofonu. Jeśli wcześniej " +
          "odmówiono zgody, odblokuj ją przy ikonie kłódki obok adresu strony.",
      );
      return false;
    }
    return true;
  }, [acquireAndAdd]);

  const stopScreenShare = useCallback(() => {
    const screen = screenStreamRef.current;
    if (!screen) return;
    screen.getTracks().forEach((t) => t.stop());
    screenStreamRef.current = null;
    setLocalScreenStream(null);
    setMediaStateBoth({ ...mediaStateRef.current, screen: false });
    // The single video sender goes back to the camera (or null) — no renegotiation.
    updateVideoSender();
    broadcastMediaState();
  }, [broadcastMediaState, setMediaStateBoth, updateVideoSender]);

  const startScreenShare = useCallback(async () => {
    if (screenStreamRef.current) return;
    let screen: MediaStream;
    try {
      screen = await navigator.mediaDevices.getDisplayMedia({
        video: true,
        audio: false,
        selfBrowserSurface: "include",
      } as DisplayMediaStreamOptions);
    } catch {
      return; // user cancelled the picker
    }
    const track = screen.getVideoTracks()[0];
    if (!track) return;
    track.contentHint = "detail";
    track.addEventListener("ended", () => stopScreenShare());
    screenStreamRef.current = screen;
    setLocalScreenStream(screen);
    setMediaStateBoth({ ...mediaStateRef.current, screen: true });
    // The single video sender now carries the screen instead of the camera —
    // replaceTrack, so NO renegotiation and no signaling storm.
    updateVideoSender();
    broadcastMediaState();
    loggerRef.current?.log("screen_share_started", {
      peers: entriesRef.current.size,
    });
  }, [broadcastMediaState, setMediaStateBoth, stopScreenShare, updateVideoSender]);

  const switchMic = useCallback(async (deviceId: string) => {
    let newTrack: MediaStreamTrack | null = null;
    try {
      const fresh = await navigator.mediaDevices.getUserMedia({
        audio: { deviceId: { exact: deviceId } },
      });
      newTrack = fresh.getAudioTracks()[0] ?? null;
      if (!newTrack) return;
      const old = cameraStreamRef.current?.getAudioTracks()[0] ?? null;
      // Picking a mic while joined WITHOUT one means "turn my sound on" —
      // inheriting the stale audio:false here attached a disabled track while
      // the UI flipped to a green mic (silence behind a live-looking icon).
      const enable = old ? mediaStateRef.current.audio : true;
      newTrack.enabled = enable;
      watchLocalTrack(newTrack, "audio");
      // Per-entry: one closed pc (peer leaving mid-switch) must not abort the
      // swap for everyone else and desync stream vs senders.
      for (const entry of entriesRef.current.values()) {
        if (!entry.transceivers) continue;
        try {
          await entry.transceivers.audio.sender.replaceTrack(newTrack);
        } catch (err) {
          loggerRef.current?.log(
            "replace_track_error",
            { kind: "audio", error: String(err) },
            entry.info.peer_id,
          );
        }
      }
      const keep = (cameraStreamRef.current?.getTracks() ?? []).filter(
        (t) => t.kind !== "audio",
      );
      old?.stop();
      // NEW stream identity so the self tile and its useSpeaking analyser
      // re-attach — mutating the old stream left the "am I capturing?" bars
      // wired to the stopped track forever.
      const next = new MediaStream([...keep, newTrack]);
      cameraStreamRef.current = next;
      setLocalCameraStream(next);
      setMediaStateBoth({ ...mediaStateRef.current, audio: enable });
      broadcastMediaState();
    } catch {
      newTrack?.stop();
      setError("Nie udało się przełączyć mikrofonu.");
    }
  }, [broadcastMediaState, setMediaStateBoth, watchLocalTrack]);

  const switchCamera = useCallback(async (deviceId: string) => {
    let newTrack: MediaStreamTrack | null = null;
    try {
      const fresh = await navigator.mediaDevices.getUserMedia({
        video: { deviceId: { exact: deviceId }, width: { ideal: 1280 }, height: { ideal: 720 } },
      });
      newTrack = fresh.getVideoTracks()[0] ?? null;
      if (!newTrack) return;
      newTrack.contentHint = "motion";
      const old = cameraStreamRef.current?.getVideoTracks()[0] ?? null;
      // Same rule as switchMic: picking a camera while none is captured means
      // "turn it on", not "inherit the stale off flag onto a dead sender".
      const enable = old ? mediaStateRef.current.video : true;
      newTrack.enabled = enable;
      watchLocalTrack(newTrack, "video");
      const keep = (cameraStreamRef.current?.getTracks() ?? []).filter(
        (t) => t.kind !== "video",
      );
      old?.stop();
      const next = new MediaStream([...keep, newTrack]);
      cameraStreamRef.current = next;
      setLocalCameraStream(next);
      setMediaStateBoth({ ...mediaStateRef.current, video: enable });
      // Push to the video sender only when the camera is the active channel
      // (i.e. not currently screen-sharing) — replaceTrack, no renegotiation.
      if (!mediaStateRef.current.screen) updateVideoSender();
      broadcastMediaState();
    } catch {
      newTrack?.stop();
      setError("Nie udało się przełączyć kamery.");
    }
  }, [broadcastMediaState, setMediaStateBoth, updateVideoSender, watchLocalTrack]);

  // UI components (tiles) report playback-level diagnostics through this — the
  // logger itself is private to the hook.
  const logEvent = useCallback(
    (type: string, payload?: Record<string, unknown>) =>
      loggerRef.current?.log(type, payload),
    [],
  );

  // ── lifecycle wiring ───────────────────────────────────────────────────────

  useEffect(() => {
    const onPageHide = () => {
      if (statusRef.current === "joining") {
        // Mid-join: let join() finish and unwind, otherwise its continuation
        // (resumed after a bfcache restore) would flip the room back to
        // "active" on top of a server-side leave.
        pendingLeaveRef.current = "page-hide";
        transport.leaveKeepalive();
        return;
      }
      if (statusRef.current === "active") {
        transport.leaveKeepalive();
        teardownRef.current("page-hide");
        // Without this a bfcache restore (Back button / mobile app switch)
        // resurrected a zombie room: status still "active", green mic, dead
        // pcs, and the server already processed our leave.
        setStatusBoth("ended");
      }
    };
    window.addEventListener("pagehide", onPageHide);
    return () => window.removeEventListener("pagehide", onPageHide);
  }, [setStatusBoth, transport]);

  // Belt & braces for headset death: some browsers/OSes never fire `ended` on
  // the dying track but do fire devicechange — sweep local tracks then.
  useEffect(() => {
    const md = navigator.mediaDevices;
    if (!md?.addEventListener) return;
    const onDeviceChange = () => {
      if (statusRef.current !== "active") return;
      for (const t of cameraStreamRef.current?.getTracks() ?? []) {
        if (t.readyState === "ended") {
          loggerRef.current?.log("local_track_ended", {
            kind: t.kind,
            via: "devicechange",
          });
          void recoverLocalTrackRef.current(t.kind as "audio" | "video");
        }
      }
    };
    md.addEventListener("devicechange", onDeviceChange);
    return () => md.removeEventListener("devicechange", onDeviceChange);
  }, []);

  useEffect(() => {
    // Re-running setup means we were NOT really unmounted (StrictMode's dev
    // mount→cleanup→mount): cancel the deferred leave the cleanup queued.
    pendingLeaveRef.current = null;
    return () => {
      if (statusRef.current === "joining") {
        // Leaving now would race the in-flight join server-side (and tearing
        // down here would strand it). Defer: join()'s checkpoints unwind it.
        pendingLeaveRef.current = "unmount";
        return;
      }
      if (statusRef.current === "active") void transport.leave();
      teardownRef.current("unmount");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const peersMemo = useMemo(() => peers, [peers]);

  return {
    status,
    error,
    me,
    peers: peersMemo,
    contract,
    localCameraStream,
    localScreenStream,
    mediaState,
    cameraAutoDisabled,
    join,
    leave,
    toggleMic,
    toggleCamera,
    requestDevices,
    startScreenShare,
    stopScreenShare,
    switchMic,
    switchCamera,
    dismissError: () => setError(null),
    logEvent,
  };
}
